import {
  color,
  height,
  marginRight,
  paddingY,
  scrollbar,
  text,
} from '@spaceship-fspl/styles';
import { TrackingAction, useTracking } from '@spaceship-fspl/tracking';
import React from 'react';
import styled from 'styled-components';

const OptionsShade = styled.div`
  height: 100%;
  position: relative;

  :after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 50%;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 1%, #ffffff 60%);
  }
`;

const OptionsViewport = styled.div`
  ${height({ xs: 'calc(100vh - 200px)', lg: '100%' })}
  ${scrollbar({
    thumbColor: 'neutral.100',
    trackColor: 'neutral.030',
  })}
  max-height: 100%;
  overflow-x: hidden;

  :focus,
  :focus ${OptionsShade}, ${OptionsShade}:focus {
    outline: none;
  }
`;

const Options = styled.div`
  ${marginRight('xxs')}
  overflow-x: hidden;
`;

const Option = styled.div`
  cursor: pointer;
  ${color('neutral.070')}
  ${text({ variant: 2 })}
  white-space: nowrap;
  ${paddingY('xxs')}

  &[aria-selected=true] {
    ${color('neutral.100')}
  }
`;

function getSelectedIndexFromDOM(list: HTMLDivElement): number {
  const options = Array.from(list.querySelectorAll('[role=option]'));
  const selectedIndex = options.findIndex(
    (option) => option.getAttribute('aria-selected') === 'true',
  );
  return selectedIndex;
}

interface ListOption {
  value: string;
  label: string;
}

export interface ListProps {
  options: ListOption[];
  selected: string | undefined;
  onSelect: (value: string | undefined) => void;
}

export interface ListRef {
  scrollToNextPage(): void;
  scrollToPrevPage(): void;
}

export const List = React.forwardRef<ListRef, ListProps>(
  ({ options, selected, onSelect, ...otherProps }, ref) => {
    const { track } = useTracking();
    const viewportRef = React.useRef<HTMLDivElement>(null);
    const listboxRef = React.useRef<HTMLDivElement>(null);

    const select = (option: ListOption | undefined): void => {
      track({
        action: TrackingAction.Select,
        label: option?.label || '',
      });
      onSelect(option?.value);
    };

    const scrollOptionIntoView = (
      value: string,
      direction: 'down' | 'up',
    ): void => {
      const viewportElement = viewportRef.current;
      const listboxElement = listboxRef.current;
      if (!viewportElement || !listboxElement) {
        return;
      }

      // find the option based on the index in the markup (because aria-selected=true won't be
      // applied until react re-renders the component)
      const selectedIndex = options.findIndex(
        (option) => value === option.value,
      );
      if (selectedIndex === -1) {
        return;
      }
      // need to add 1 to selectedIndex because :nth-of-type starts at 1
      const optionElement = listboxElement.querySelector<HTMLDivElement>(
        `:scope > [role=option]:nth-of-type(${selectedIndex + 1})`,
      );
      if (!optionElement) {
        return;
      }

      const viewportHeight = viewportElement.getBoundingClientRect().height;
      const optionHeight = optionElement.getBoundingClientRect().height;

      if (direction === 'down') {
        // if option is below the viewport then scroll down enough to fit the option
        // at the bottom of the viewport
        if (
          optionElement.offsetTop + optionHeight >
          viewportElement.scrollTop + viewportHeight
        ) {
          viewportElement.scrollTo({
            // smooth scroll looks laggy when doing it frequently
            behavior: 'auto',
            top: Math.max(
              optionElement.offsetTop + optionHeight - viewportHeight,
              0,
            ),
          });
        }
        return;
      }

      if (direction === 'up') {
        // if option is above the viewport then scroll up enough to fit the option
        // at the top of the viewport
        if (optionElement.offsetTop < viewportElement.scrollTop) {
          viewportElement.scrollTo({
            // smooth scroll looks laggy when doing it frequently
            behavior: 'auto',
            top: optionElement.offsetTop,
          });
        }
        return;
      }
    };

    const handleFocus = (): void => {
      if (options.length !== 0 && selected === undefined) {
        select(options[0]);
      }
    };

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>,
    ): void => {
      if (options.length === 0) {
        return;
      }

      const firstOption = options[0];
      if (event.key === 'Home' && firstOption) {
        event.preventDefault();
        select(firstOption);
        scrollOptionIntoView(firstOption.value, 'up');
      }

      const lastOption = options[options.length - 1];
      if (event.key === 'End' && lastOption) {
        event.preventDefault();
        select(lastOption);
        scrollOptionIntoView(lastOption.value, 'down');
      }

      if (selected === undefined) {
        return;
      }
      if (event.key === 'ArrowDown' && lastOption) {
        event.preventDefault();
        const selectedIndex = getSelectedIndexFromDOM(event.currentTarget);
        if (selectedIndex !== -1) {
          const selectedOption = options[selectedIndex + 1];
          select(selectedOption || lastOption);
          scrollOptionIntoView((selectedOption || lastOption).value, 'down');
        }
      }
      if (event.key === 'ArrowUp' && firstOption) {
        event.preventDefault();
        const selectedIndex = getSelectedIndexFromDOM(event.currentTarget);
        if (selectedIndex !== -1) {
          const selectedOption = options[selectedIndex - 1];
          select(selectedOption || firstOption);
          scrollOptionIntoView((selectedOption || firstOption).value, 'up');
        }
      }
    };

    const handlePageDown = React.useCallback((): void => {
      const viewportElement = viewportRef.current;
      const listboxElement = listboxRef.current;
      if (!viewportElement || !listboxElement) {
        return;
      }
      const viewportHeight = viewportElement.getBoundingClientRect().height;
      const listboxHeight = listboxElement.getBoundingClientRect().height;
      // scroll down by the height of the viewport OR till we reach the bottom of the list
      viewportElement.scrollTo({
        behavior: 'smooth',
        top: Math.min(
          viewportElement.scrollTop + viewportHeight,
          listboxHeight - viewportHeight,
        ),
      });
    }, []);

    const handlePageUp = React.useCallback((): void => {
      const viewportElement = viewportRef.current;
      const listboxElement = listboxRef.current;
      if (!viewportElement || !listboxElement) {
        return;
      }
      // scroll up by the height of the viewport OR till we reach the top of the list
      const viewportHeight = viewportElement.getBoundingClientRect().height;
      viewportElement.scrollTo({
        behavior: 'smooth',
        top: Math.max(viewportElement.scrollTop - viewportHeight, 0),
      });
    }, []);

    React.useImperativeHandle(
      ref,
      () => ({
        scrollToNextPage: handlePageDown,
        scrollToPrevPage: handlePageUp,
      }),
      [handlePageDown, handlePageUp],
    );

    return (
      <OptionsViewport
        ref={viewportRef}
        {...otherProps}
        role="listbox"
        tabIndex={0}
        aria-activedescendant={
          selected !== undefined
            ? `investment-companies-company-${selected}`
            : undefined
        }
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      >
        <Options ref={listboxRef}>
          {options.map((option) => (
            <Option
              key={option.value}
              id={`investment-companies-company-${option.value}`}
              role="option"
              aria-selected={selected === option.value ? 'true' : 'false'}
              onClick={() => select(option)}
            >
              <OptionsShade tabIndex={-1}>{option.label}</OptionsShade>
            </Option>
          ))}
        </Options>
      </OptionsViewport>
    );
  },
);

List.displayName = 'List';
